// The Apps and share pages' calls, through the generated client (never hand-written requests).
import type { paths } from '@cortex/api-client';
import { api } from '../../platform/index.ts';
import { call } from '../../platform/index.ts';

type Json<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  responses: { 200: { content: { 'application/json': infer T } } };
}
  ? T
  : paths[P][M] extends { responses: { 201: { content: { 'application/json': infer T } } } }
    ? T
    : never;

export type App = Json<'/api/apps/projects/{slug}', 'get'>['apps'][number];
export type AppShare = Json<'/api/projects/{slug}/apps/{app}/shares', 'get'>['shares'][number];

export const apps = {
  list: (slug: string, branch: string, includeRemoved = false) =>
    call(
      api.GET('/api/apps/projects/{slug}', {
        params: { path: { slug }, query: { branch, ...(includeRemoved ? { includeRemoved } : {}) } },
      }),
    ),
  rename: (slug: string, name: string, title: string | null) =>
    call(
      api.PUT('/api/apps/projects/{slug}/apps/{name}', { params: { path: { slug, name } }, body: { title } }),
    ),
  remove: (slug: string, name: string) =>
    call(api.DELETE('/api/apps/projects/{slug}/apps/{name}', { params: { path: { slug, name } } })),
  restore: (slug: string, name: string) =>
    call(api.POST('/api/apps/projects/{slug}/apps/{name}/restore', { params: { path: { slug, name } } })),
  shares: (slug: string, app: string) =>
    call(api.GET('/api/projects/{slug}/apps/{app}/shares', { params: { path: { slug, app } } })),
  share: (slug: string, app: string, email: string, role: 'viewer' | 'editor' = 'viewer') =>
    call(
      api.POST('/api/projects/{slug}/apps/{app}/shares', {
        params: { path: { slug, app } },
        body: { email, role },
      }),
    ),
  /** A pending share's invitation again: mailed, or (`link`) its new link handed back to pass on. */
  resendInvitation: (slug: string, app: string, id: string, delivery: 'mail' | 'link') =>
    call(
      api.POST('/api/projects/{slug}/apps/{app}/share-invitations/{id}/resend', {
        params: { path: { slug, app, id } },
        body: { delivery },
      }),
    ),
  /** Withdraws a pending share (and its invitation, when it carried only this app). */
  revokeInvitation: (slug: string, app: string, id: string) =>
    call(
      api.DELETE('/api/projects/{slug}/apps/{app}/share-invitations/{id}', {
        params: { path: { slug, app, id } },
      }),
    ),
  unshare: (slug: string, app: string, userId: string) =>
    call(
      api.DELETE('/api/projects/{slug}/apps/{app}/shares/{userId}', {
        params: { path: { slug, app, userId } },
      }),
    ),
  /** A copy of the app to change safely: a branch of the project (the same app on it, the same data). */
  copyAsBranch: (slug: string, name: string, from: string) =>
    call(
      api.POST('/api/workspace/projects/{slug}/branches', {
        params: { path: { slug } },
        body: { name, from },
      }),
    ),
  get: (slug: string, name: string, branch: string) =>
    call(
      api.GET('/api/apps/projects/{slug}/apps/{name}', {
        params: { path: { slug, name }, query: { branch } },
      }),
    ),
};
